import React, { useState } from 'react';
import styled from 'styled-components';
import { Row, Col } from 'react-bootstrap';
import { LoadingSpinner } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import useConfig from '../../../../utils/useConfig';
import usePlayerSelector from '../../../../utils/usePlayerSelector';
import { useOktaQueries, Player, Years, Levels, Stats } from '../../../../data';
import ReportCard from '../../../../components/ReportCard';
import CatcherStrengthsFilters from '../CatcherStrengthsFilters';
import ThrowingTable from './ThrowingTable';
import ThrowingTabs from './ThrowingTabs';

const StyledRow = styled(Row)`
    margin: 10px;
`;
const StyledContainer = styled.div`
text-align: left;
padding: 10 px;
margin: 5px;
`;

const buildQueries = (playerid, currentYear, usePreviousYearData, currentLevelID, filters) => {
    const startyear = filters?.startYear?.id || currentYear - (usePreviousYearData ? 1 : 0);
    const endyear = filters?.endYear?.id || currentYear;
    const levelid = filters?.level?.id || currentLevelID;
    const queries = [
        {
            queryKey: ['player', playerid],
            queryFn: (t) => Player.get(t, { playerId: playerid }),
        },
        {
            queryKey: ['years'],
            queryFn: (t) => Years.get(t, { haspitchfx: 2 }),
        },
        {
            queryKey: ['levels'],
            queryFn: (t) => Levels.get(t, { haspitchfx: 2 }),
        },
        {
            queryKey: ['catcherThrows', playerid],
            queryFn: (t) => Stats.catcherThrows(t, { playerid }),
        },
        {
            queryKey: ['catcherCharts', playerid, startyear, endyear, levelid],
            queryFn: (t) => Stats.catcherCharts(t, { playerid, startyear, endyear, levelid }),
        },
    ];
    return queries;
};

const mapDataResponse = (response, currentYear, usePreviousYearData, currentLevelID, filters) => {
    const startyear = filters?.startYear?.id || currentYear - (usePreviousYearData ? 1 : 0);
    const endyear = filters?.endYear?.id || currentYear;
    const levelid = filters?.level?.id || currentLevelID;

    const player = response[0].data;
    const years = response[1].data;
    const levels = response[2].data;
    const catcherThrowData = response[3].data;
    let { catcherthrows, catcherpickoffthrows } = catcherThrowData;
    catcherthrows = catcherthrows?.filter((t) => t.levelid === levelid && t.yearid >= startyear && t.yearid <= endyear) || [];
    catcherpickoffthrows = catcherpickoffthrows?.filter((t) => t.levelid === levelid && t.yearid >= startyear && t.yearid <= endyear) || [];
    const catcherCharts = response[4].data;
    const { splits, mlb } = catcherCharts;
    const playerTableData = splits.find((s) => s.base === 2) || null;
    const lgavgTableData = mlb.find((s) => s.base === 2) || null;

    return { player, years, levels, catcherThrows: catcherthrows, catcherPickoffThrows: catcherpickoffthrows, playerTableData, lgavgTableData };
};

const Throwing = () => {
    const { t: translate } = useTranslation();
    const { PlayerID, Team } = usePlayerSelector();
    const { Level: CurrentLevel } = Team;
    const { CurrentYear, UsePreviousYearData } = useConfig();
    const [filters, setFilters] = useState({ startYear: null, endYear: null, level: null });

    const dataQueries = buildQueries(PlayerID, CurrentYear, CurrentLevel.LevelID, filters);
    const dataQueriesResponse = useOktaQueries(dataQueries);

    if (dataQueriesResponse.some((q) => q.isLoading)) {
        return <LoadingSpinner />;
    }

    const { player, years, levels, catcherThrows, catcherPickoffThrows, playerTableData, lgavgTableData } = mapDataResponse(dataQueriesResponse, CurrentYear, UsePreviousYearData, CurrentLevel.LevelID, filters);
    const yearOptions = years.map((y) => ({ id: y.YearID, name: y.YearID }));
    const levelOptions = levels.map((l) => ({ id: l.LevelID, name: l.Abbr }));

    const startYear = filters.startYear || yearOptions[UsePreviousYearData ? 1 : 0];
    const endYear = filters.endYear || yearOptions[0];
    const level = filters.level || levelOptions.find((l) => l.id === Team.Level.LevelID);

    return (
        <>
            <CatcherStrengthsFilters
                startYearOptions={yearOptions}
                endYearOptions={yearOptions}
                levelOptions={levelOptions}
                startYear={startYear}
                endYear={endYear}
                level={level}
                onSubmit={(updatedFilters) => setFilters(updatedFilters)}
            />
            <StyledRow>
                <Col sm={{ span: 12 }}>
                    <ReportCard title={`${translate('sections.reports.catcher.throwing.catcher-2b')} ${startYear.id === endYear.id ? startYear.id : `${startYear.id} - ${endYear.id}`}`}>
                        <StyledContainer>
                            <ThrowingTable player={player} catcherData={playerTableData} lgavgData={lgavgTableData} />
                        </StyledContainer>
                    </ReportCard>
                </Col>
            </StyledRow>
            <StyledRow>
                <Col sm={{ span: 12 }}>
                    <ThrowingTabs
                        player={player}
                        startYear={startYear.id}
                        endYear={endYear.id}
                        catcherThrows={catcherThrows}
                        catcherPickoffThrows={catcherPickoffThrows}
                    />
                </Col>
            </StyledRow>
        </>
    );
};

export default Throwing;
